import { useState } from 'react'
import {
  emptyPersonFacts,
  HOUSEHOLD_FACT_REGISTRY,
  isHouseholdFactStale,
  isPersonFactStale,
  PERSON_FACT_REGISTRY,
  PERSON_ROLE_LABEL,
  PERSON_ROLES,
  valueOf,
  type Answers,
  type Assessment,
  type Fact,
  type HouseholdFactField,
  type Person,
  type PersonFactField,
  type PersonRole,
} from '@asvise/household-engine/vocabulary'
import { Answering, unanswered, type Ask, type Asking } from './Answering'
import { Question, Section, TextField } from './fields'
import { answerOf, asksPerson, personAnswerOf, usedBy } from './inputs'
import { PHASES, UNPLACED, type Phase } from './phases'

/**
 * The record of what was asked, and a way to answer any of it.
 *
 * This was a page of sixteen inputs and four repeated member cards, and that is
 * the screen the business app deliberately stopped having: presenting a record
 * as a wall of controls shows a form to somebody who came to read. Its note
 * says it plainly, and it is the same sentence this file needed.
 *
 * So the same two moves. The questions are grouped into the framework's own
 * four lettered sets rather than into categories somebody invented, which is
 * `phases.ts`. And each question is a row saying what is recorded and when,
 * which opens the sheet the board already uses. Nothing on this screen is an
 * input, so nothing on it can be half-typed, and a phase of four questions is
 * asked the way an exposure's four are.
 *
 * A PHASE IS A TOPIC RATHER THAN A SUBJECT. Group A asks three things about
 * each member and nothing about the household; group C the reverse; B and D
 * both. That is the document's grouping, and it is why the members are no
 * longer a card of their own with every question about them inside it: who is
 * in the household is identity, and what is true of them is answered in the
 * phase that asks it.
 *
 * WHAT IS NOT ASKED, and the absence is the design. No diagnosis, no medical
 * history, no per-person salary. The household figure and one income share
 * carry everything the rules need, and a health declaration belongs in an
 * insurer's authorised application rather than here.
 */

interface Props {
  answers: Answers
  onChange: (next: Answers) => void
}

interface FormProps extends Props {
  /** The screening, so the sheet can say what an answer settles. */
  assessment: Assessment | null
  /**
   * Now, for deciding which answers the screening has stopped relying on.
   *
   * Passed in rather than read here so one instant decides the whole screen: a
   * row calling `Date.now()` for itself could sit on either side of a staleness
   * window from the row above it.
   */
  at: number
}

/**
 * Every question one phase holds, household first and then each member's.
 *
 * One list, built once, because the rows below are rendered from it and the
 * sheet is opened into it by position. Two lists in the same order is the
 * arrangement where a row opens the question above it.
 *
 * Progressive disclosure is applied here, from `asksPerson`: framework section
 * 4.1, stated per field in the table rather than as nesting in a screen. A
 * share of income is not asked of somebody who does not earn, so it is neither
 * a row nor a question in the sheet.
 *
 * UNLESS IT ALREADY HOLDS AN ANSWER, which is the business app's rule and it is
 * worth taking whole: a fact with a value shows it whatever the gate now says.
 * Somebody who said they earned, gave a share of the income and then said they
 * no longer earn has a share on the record, and hiding the row would be this
 * screen quietly holding an answer it declines to show. A contradiction is for
 * the reader to resolve, not for the screen to cover.
 */
function asksOf(phase: Phase, answers: Answers): Ask[] {
  return [
    ...phase.household.map((field): Ask => ({ kind: 'HOUSEHOLD', field })),
    ...answers.people.flatMap((person) =>
      phase.person
        .filter(
          (field) => asksPerson(person, field) || valueOf(person.facts[field] as never) !== null,
        )
        .map((field): Ask => ({ kind: 'PERSON', person, field })),
    ),
  ]
}

/**
 * When an answer was given, read without caring which field it came from.
 *
 * Each field is typed with its own value, so reaching for `observed_at` across
 * a registry gives a union of a dozen `Fact<T>` that narrows to none of them.
 * Nothing here reads the value, so the cast loses nothing.
 */
const anyFact = (fact: unknown): Fact<unknown> | null => (fact ?? null) as Fact<unknown> | null

/**
 * Whether the screening has stopped relying on an answer.
 *
 * Two functions because the windows live in two registries: a household figure
 * and a member's answer go off at different rates, and six months on a savings
 * figure is not twelve on a year of birth.
 */
const staleHousehold = (answers: Answers, field: HouseholdFactField, at: number): boolean => {
  const fact = anyFact(answers[field])
  return fact === null ? false : isHouseholdFactStale(fact, field, at)
}

const stalePerson = (person: Person, field: PersonFactField, at: number): boolean => {
  const fact = anyFact(person.facts[field])
  return fact === null ? false : isPersonFactStale(fact, field, at)
}

function Row({
  label,
  value,
  at,
  stale,
  uses,
  onOpen,
}: {
  label: string
  value: string | null
  /** When the answer was given, where there is one. */
  at: string | null
  /**
   * Given long enough ago that the screening asks again rather than relying on
   * it, by the window the registry sets per field.
   *
   * On the row rather than on a screen of its own. It is the second of the two
   * things worth saying about a recorded answer, and a reader looking at the
   * answer is the person who needs to know it is being disregarded.
   */
  stale: boolean
  /**
   * The protection areas whose reasoning reads this answer, by the engine's own
   * account.
   *
   * Here rather than on the question, because this is the screen with the
   * problem: twenty questions about money, savings and horizons, and no way to
   * tell which of six areas any of them feeds or why the record asks a third
   * time what a month costs. A sheet is opened from the one thing that wanted
   * the answer and already says so.
   */
  uses: string[]
  onOpen: () => void
}) {
  return (
    <button type="button" className={value === null ? 'record-row empty' : 'record-row'} onClick={onOpen}>
      <span className="record-label">{label}</span>
      <span className="record-value">
        {value ?? 'Not known'}
        {stale && (
          <span className="record-because">
            Given long enough ago that the screening asks again rather than relying on it.
          </span>
        )}
        {uses.length > 0 && (
          <span className="record-uses">Used by {uses.map((u) => u.toLowerCase()).join(', ')}</span>
        )}
      </span>
      {/*
        When, and not where from. Nothing writes a household answer but the
        household: row level security permits only `source = 'USER'` on a client
        insert, so a source column here would hold one word repeated twenty
        times. The provenance that genuinely varies is on the policies.
      */}
      <span className="record-source">
        {at === null
          ? 'Add'
          : new Date(at).toLocaleDateString('en-SG', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
      </span>
    </button>
  )
}

/**
 * The head of a part of the record, which is also what opens and closes it.
 *
 * A real button inside the heading, so the heading stays a heading to anything
 * that reads the outline and the toggle is reachable and named by keyboard and
 * screen reader alike. `aria-expanded` says which state it is in and
 * `aria-controls` says what it governs.
 *
 * What is left sits beside it and is always visible, collapsed or not: closing
 * a part must not hide whether it still wants anything, or the reason to open
 * it.
 */
function GroupHead({
  anchor,
  title,
  expanded,
  onToggle,
  status,
}: {
  anchor: string
  title: string
  expanded: boolean
  onToggle: () => void
  status: React.ReactNode
}) {
  return (
    <header className="record-group-head">
      <h3>
        <button
          type="button"
          className="record-toggle"
          aria-expanded={expanded}
          aria-controls={`${anchor}-body`}
          onClick={onToggle}
        >
          <span className="record-chevron" aria-hidden="true">
            ›
          </span>
          {title}
        </button>
      </h3>
      {status}
    </header>
  )
}

function PhaseGroup({
  phase,
  anchor,
  expanded,
  onToggle,
  answers,
  assessment,
  at,
  onAsk,
}: {
  phase: Phase
  /** This group's element id, which its toggle points at. Passed in because the fallback group borrows a phase's code. */
  anchor: string
  expanded: boolean
  onToggle: () => void
  answers: Answers
  assessment: Assessment | null
  at: number
  onAsk: (asking: Asking) => void
}) {
  const asks = asksOf(phase, answers)
  if (asks.length === 0) return null

  const open = unanswered(asks, answers)
  const title = phase.title
  const ask = (at: number) => onAsk({ title, asks, at })

  /** The members this phase has anything to ask about, in household order. */
  const members = answers.people.filter((p) => asks.some((a) => a.kind === 'PERSON' && a.person.id === p.id))

  return (
    <section className="record-group" id={anchor}>
      <GroupHead
        anchor={anchor}
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        /* What is outstanding, as a count rather than as a prompt. The business
           app's zone chip, and for the same reason: a group saying "3 to answer"
           is a reader's own choice of where to go next. A finished one says so,
           because a closed group with nothing beside it cannot be told from one
           that has not been looked at. */
        status={
          open.length > 0 ? (
            <span className="record-count">{open.length} to answer</span>
          ) : (
            <span className="record-done">✓ All answered</span>
          )
        }
      />
      <div id={`${anchor}-body`} hidden={!expanded}>
      <p className="record-group-hint">{phase.hint}</p>

      {/*
        Rendered from the same list the sheet is opened into, carrying each
        row's position with it. Looking the position up again by field was the
        arrangement where a row opens the question above it the moment anything
        about the ordering changes.
      */}
      {asks.flatMap((a, index) =>
        a.kind === 'HOUSEHOLD'
          ? [
              <Row
                key={a.field}
                label={HOUSEHOLD_FACT_REGISTRY[a.field].label}
                value={answerOf(a.field, answers)}
                at={anyFact(answers[a.field])?.observed_at ?? null}
                stale={staleHousehold(answers, a.field, at)}
                uses={usedBy(assessment, a.field)}
                onOpen={() => ask(index)}
              />,
            ]
          : [],
      )}

      {members.map((person) => (
        <div className="record-who" key={person.id}>
          <h4>{person.label || PERSON_ROLE_LABEL[person.role]}</h4>
          {asks.flatMap((a, index) =>
            a.kind === 'PERSON' && a.person.id === person.id
              ? [
                  <Row
                    key={a.field}
                    label={PERSON_FACT_REGISTRY[a.field].label}
                    value={personAnswerOf(person, a.field)}
                    at={anyFact(person.facts[a.field])?.observed_at ?? null}
                    stale={stalePerson(person, a.field, at)}
                    /* Nothing for a member's answer: the engine reports the
                       fields a calculation reads, and those are the
                       household's. A year of birth reaches the rules through
                       the derived profile rather than being named by any
                       calculation, so a line here would be guesswork. */
                    uses={[]}
                    onOpen={() => ask(index)}
                  />,
                ]
              : [],
          )}
        </div>
      ))}

      {/* The group reports; answering happens with room, on its own surface. */}
      {open.length > 0 ? (
        <button type="button" className="record-answer" onClick={() => ask(asks.indexOf(open[0]!))}>
          Answer {open.length} {open.length === 1 ? 'question' : 'questions'} →
        </button>
      ) : (
        <button type="button" className="record-edit" onClick={() => ask(0)}>
          Change answers
        </button>
      )}
      </div>
    </section>
  )
}

/**
 * How much is left overall, and a way to open or close everything.
 *
 * A row of links to each part used to stand here, kept at the top as the page
 * scrolled, each with the count its part would show. Once the parts could be
 * closed it said what the headings already say: a collapsed record is five
 * lines, each carrying its own count, so the index was a second copy of the
 * thing it indexed. What the headings cannot say is the total, and closing or
 * opening all of them is one click here rather than five.
 */
function RecordControls({
  total,
  allOpen,
  onToggleAll,
}: {
  total: number
  allOpen: boolean
  onToggleAll: () => void
}) {
  return (
    <div className="record-controls">
      <span>{total > 0 ? `${total} to answer` : 'Everything answered'}</span>
      <button type="button" className="link" onClick={onToggleAll}>
        {allOpen ? 'Collapse all' : 'Expand all'}
      </button>
    </div>
  )
}

/** What the toggles call the household's own part. */
const HOUSEHOLD = 'record-household'

/**
 * The names a record has before anybody has named it.
 *
 * Both, because the column's default moved from "My household" to "My family"
 * when the product started saying family, and rows written before that keep the
 * old one. Either means it has not been named, which decides whether the
 * family's own part opens or stays closed. Copied from SQL, since the client
 * never set it.
 */
const DEFAULT_NAMES = ['My household', 'My family']
export const isNamed = (name: string): boolean =>
  name.trim() !== '' && !DEFAULT_NAMES.includes(name.trim())

export function Household({ answers, onChange, assessment, at }: FormProps) {
  const [asking, setAsking] = useState<Asking | null>(null)

  /**
   * A phase the registry's numbering did not place, which should never happen.
   *
   * Rendered rather than dropped. A question in the registry that no screen
   * asks is the failure this codebase keeps meeting, and a grouping that can
   * silently exclude one is the same failure with a tidier screen in front of
   * it. `phases.test.ts` fails on this; this is so a build that somehow ships
   * still asks the question.
   */
  const leftOver: Phase | null =
    UNPLACED.household.length > 0 || UNPLACED.person.length > 0
      ? {
          code: 'A',
          title: 'Anything else we ask',
          hint: 'Questions that do not belong to one of the groups above.',
          framework: '',
          household: UNPLACED.household,
          person: UNPLACED.person,
        }
      : null

  /**
   * What is still to answer in each group, which the total adds up.
   *
   * The same list the group builds its own count from, so the total and the
   * numbers on the headings cannot disagree: both come from `asksOf` and
   * `unanswered`, and a member who earns adds a question to both at once.
   */
  const groups = [...PHASES, ...(leftOver ? [leftOver] : [])].map((phase, i) => ({
    phase,
    anchor: leftOver && i === PHASES.length ? 'record-other' : `record-${phase.code}`,
    open: unanswered(asksOf(phase, answers), answers).length,
  }))
  const total = groups.reduce((n, g) => n + g.open, 0)

  /**
   * Which parts are closed, decided once when the screen opens.
   *
   * What still has something to answer is open and what is finished is closed,
   * so a household that has done most of it sees what is left rather than a
   * page of answers it already gave, and a new one sees everything, as before.
   * The household's own part is open until it has been named.
   *
   * NEVER CHANGED BY ITSELF AFTERWARDS. A part that closed the moment its last
   * question was answered would take its rows out from under the cursor, which
   * is the thing the business board pins its arrangement to avoid. Only a click
   * moves a part.
   */
  const [closed, setClosed] = useState<Set<string>>(
    () =>
      new Set([
        ...groups.filter((g) => g.open === 0).map((g) => g.anchor),
        ...(isNamed(answers.household_name) ? [HOUSEHOLD] : []),
      ]),
  )

  const toggle = (anchor: string) =>
    setClosed((held) => {
      const next = new Set(held)
      if (next.has(anchor)) next.delete(anchor)
      else next.add(anchor)
      return next
    })

  const everything = [HOUSEHOLD, ...groups.map((g) => g.anchor)]
  const allOpen = everything.every((a) => !closed.has(a))

  return (
    <div className="record">
      <RecordControls
        total={total}
        allOpen={allOpen}
        onToggleAll={() => setClosed(allOpen ? new Set(everything) : new Set())}
      />

      {/*
        The household's name and its members, in the record's own column. The
        name used to be rendered by the screen that holds this, outside the
        container that sets the width, so its box ran to the edge of the page
        while everything under it sat in a narrower column and the two right
        edges never met.
      */}
      <section className="record-group" id={HOUSEHOLD}>
        <GroupHead
          anchor={HOUSEHOLD}
          title="The family"
          expanded={!closed.has(HOUSEHOLD)}
          onToggle={() => toggle(HOUSEHOLD)}
          /* Who it is, so a closed part still says what is in it. */
          status={
            <span className="record-done">
              {isNamed(answers.household_name) ? answers.household_name.trim() : 'Not named yet'}
              {' · '}
              {answers.people.length} {answers.people.length === 1 ? 'person' : 'people'}
            </span>
          }
        />
        <div id={`${HOUSEHOLD}-body`} hidden={closed.has(HOUSEHOLD)}>
          <Section title="What shall we call your family?">
            <Question title="Name for your family">
              <TextField
                value={answers.household_name}
                onChange={(v) => onChange({ ...answers, household_name: v })}
              />
            </Question>
          </Section>

          <People answers={answers} onChange={onChange} />
        </div>
      </section>

      {groups.map(({ phase, anchor }) => (
        <PhaseGroup
          key={anchor}
          phase={phase}
          anchor={anchor}
          expanded={!closed.has(anchor)}
          onToggle={() => toggle(anchor)}
          answers={answers}
          assessment={assessment}
          at={at}
          onAsk={setAsking}
        />
      ))}

      {asking && (
        <Answering
          asking={asking}
          answers={answers}
          onChange={onChange}
          assessment={assessment}
          onDone={() => setAsking(null)}
        />
      )}
    </div>
  )
}

/**
 * The members, added and removed rather than answered.
 *
 * Exported because the first screen asks for them and nothing else: who is in
 * the household is the one answer that makes the board say anything, in the
 * way that nature of business is for the commercial app.
 *
 * Identity only, now that the phases ask the questions. A member is added,
 * named and leaves, and none of those is an answer to a question: it is the
 * reason the record has a `people` table rather than a column, and the one
 * screen with no commercial counterpart. What is true of them is asked in the
 * phase the framework asks it in.
 */
export function People({ answers, onChange }: Props) {
  const setPerson = (id: string, next: Partial<Person>) =>
    onChange({
      ...answers,
      people: answers.people.map((p) => (p.id === id ? { ...p, ...next } : p)),
    })

  const addPerson = (role: PersonRole) =>
    onChange({
      ...answers,
      people: [
        ...answers.people,
        {
          id: crypto.randomUUID(),
          label: suggestLabel(role, answers.people),
          role,
          facts: emptyPersonFacts(),
        },
      ],
    })

  const removePerson = (id: string) =>
    onChange({ ...answers, people: answers.people.filter((p) => p.id !== id) })

  return (
    <Section
      title="Who lives here"
      hint="Everyone whose money this is, and everyone who depends on it. Living on your own counts: a family of one is still a family."
    >
      <div className="people">
        {answers.people.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            onRename={(label) => setPerson(person.id, { label })}
            onRemove={answers.people.length > 1 ? () => removePerson(person.id) : undefined}
          />
        ))}
      </div>

      <div className="add-person">
        {PERSON_ROLES.filter(
          (r) => r !== 'SELF' || answers.people.every((p) => p.role !== 'SELF'),
        ).map((role) => (
          <button key={role} type="button" className="secondary" onClick={() => addPerson(role)}>
            Add {PERSON_ROLE_LABEL[role].toLowerCase()}
          </button>
        ))}
      </div>
    </Section>
  )
}

function PersonCard({
  person,
  onRename,
  onRemove,
}: {
  person: Person
  onRename: (label: string) => void
  onRemove?: () => void
}) {
  return (
    <div className="person">
      <div className="person-head">
        <h3>{person.label || PERSON_ROLE_LABEL[person.role]}</h3>
        <span className="person-role">{PERSON_ROLE_LABEL[person.role]}</span>
        {onRemove && (
          <button type="button" className="link danger" onClick={onRemove}>
            Remove from this record
          </button>
        )}
      </div>

      <div className="person-facts">
        {/*
          A full legal name, which is what a policy prints. It was "What do you
          call them?", which got a nickname or a role, so the household picked
          the person a schedule names out of a list reading "You", "Partner" and
          "Child". Nothing compares the two: the engine only echoes this back for
          display, and matching is the household's own choice.

          It was "Full name" with a hint saying a first name is enough, and the
          owner asked for the plain legal wording instead. Worth knowing what
          that trades: the framework calls the policyholder a "user-confirmed
          name" and puts identity verification with the adviser, so "legal"
          implies a check this product does not make, and it asks for a child's
          name as it stands in an identity document where the record otherwise
          asks for a year of birth and never a date. Nothing requires it.
        */}
        <Question title="Full legal name">
          <TextField value={person.label} onChange={onRename} />
        </Question>
      </div>
    </div>
  )
}

function suggestLabel(role: PersonRole, people: Person[]): string {
  if (role === 'SELF') return 'You'
  const same = people.filter((p) => p.role === role).length
  const base = PERSON_ROLE_LABEL[role]
  return same === 0 ? base : `${base} ${same + 1}`
}

/**
 * Whether the board has anybody to be about.
 *
 * Somebody, and not somebody with a year of birth. It asked for the year too at
 * first, which made a household that had added a person and not yet typed a
 * date sit on the first screen through every reload with one line of small
 * print explaining why. That is the questionnaire's old failure in a new place:
 * a screen withholding everything until it is satisfied.
 *
 * The board is the thing that asks. A member with no year gives six areas that
 * say what they need, and the age is one of the things they ask for, which is
 * exactly what the board is for.
 */
export const hasSomebody = (answers: Answers): boolean => answers.people.length > 0

export { PERSON_FACT_REGISTRY }
